import { useState } from 'react';

export type SignalFilter = 'all' | 'BUY' | 'SELL' | 'HOLD' | 'none' | 'protected';
export type SpreadFilter = 'all' | 'lte1' | 'lte1_5';
export type StatusFilter = 'all' | 'hasAgent' | 'hasPosition' | 'protected';

export interface FilterState {
  signal: SignalFilter;
  spread: SpreadFilter;
  status: StatusFilter;
}

export const DEFAULT_FILTER: FilterState = { signal: 'all', spread: 'all', status: 'all' };

const SIGNAL_OPTIONS: { key: SignalFilter; label: string; color: string; bg: string }[] = [
  { key: 'all', label: '全部方向', color: '#6B7280', bg: '#F3F4F6' },
  { key: 'BUY', label: 'BUY', color: '#16A34A', bg: '#DCFCE7' },
  { key: 'SELL', label: 'SELL', color: '#E11D48', bg: '#FFE4E6' },
  { key: 'HOLD', label: 'HOLD', color: '#475569', bg: '#EEF2FF' },
  { key: 'none', label: '无信号', color: '#6B7280', bg: '#F3F4F6' },
  { key: 'protected', label: '事件保护', color: '#D97706', bg: '#FEF3C7' },
];

const SPREAD_OPTIONS: { key: SpreadFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'lte1', label: '≤ 1.0（按品种单位）' },
  { key: 'lte1_5', label: '≤ 1.5（按品种单位）' },
];

const STATUS_OPTIONS: { key: StatusFilter; label: string; icon: string }[] = [
  { key: 'all', label: '全部', icon: 'ri-checkbox-blank-circle-line' },
  { key: 'hasAgent', label: '有 AI 智能体', icon: 'ri-robot-2-line' },
  { key: 'hasPosition', label: '有持仓', icon: 'ri-briefcase-5-line' },
  { key: 'protected', label: '事件保护', icon: 'ri-shield-cross-line' },
];

function countFilters(f: FilterState): number {
  let c = 0;
  if (f.signal !== 'all') c++;
  if (f.spread !== 'all') c++;
  if (f.status !== 'all') c++;
  return c;
}

interface FilterPanelProps {
  value: FilterState;
  onChange: (f: FilterState) => void;
  onClose: () => void;
}

export default function FilterPanel({ value, onChange, onClose }: FilterPanelProps) {
  const [draft, setDraft] = useState<FilterState>(value);

  const apply = () => {
    onChange(draft);
    onClose();
  };

  const reset = () => {
    setDraft(DEFAULT_FILTER);
  };

  const filterCount = countFilters(draft);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl"
        style={{
          boxShadow: '0 -4px 32px rgba(0,0,0,0.1)',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3">
          <h3 className="text-gray-900 font-bold text-base">
            筛选
            {filterCount > 0 && (
              <span className="ml-1.5 text-xs font-medium text-indigo-500">({filterCount})</span>
            )}
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={reset}
              className="text-gray-400 text-xs font-medium"
            >
              重置
            </button>
            <button
              onClick={apply}
              className="px-4 py-1.5 rounded-full text-xs font-semibold text-white"
              style={{ background: '#6366F1' }}
            >
              应用
            </button>
          </div>
        </div>

        <div className="px-5 flex flex-col gap-5 pb-4">
          {/* 信号方向 */}
          <div>
            <p className="text-gray-500 text-xs font-medium mb-2.5">信号方向</p>
            <div className="flex flex-wrap gap-2">
              {SIGNAL_OPTIONS.map((opt) => {
                const isActive = draft.signal === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setDraft((d) => ({ ...d, signal: opt.key }))}
                    className="px-3 py-2 rounded-full text-xs font-medium transition-all border"
                    style={{
                      background: isActive ? opt.bg : '#fff',
                      color: isActive ? opt.color : '#6B7280',
                      borderColor: isActive ? opt.color : '#E5E7EB',
                      opacity: isActive ? 1 : 0.9,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 点差区间 */}
          <div>
            <p className="text-gray-500 text-xs font-medium mb-2.5">点差区间</p>
            <div className="flex flex-wrap gap-2">
              {SPREAD_OPTIONS.map((opt) => {
                const isActive = draft.spread === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setDraft((d) => ({ ...d, spread: opt.key }))}
                    className="px-3 py-2 rounded-full text-xs font-medium transition-all border"
                    style={{
                      background: isActive ? '#EEF2FF' : '#fff',
                      color: isActive ? '#6366F1' : '#6B7280',
                      borderColor: isActive ? '#6366F1' : '#E5E7EB',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 状态 */}
          <div>
            <p className="text-gray-500 text-xs font-medium mb-2.5">状态</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((opt) => {
                const isActive = draft.status === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setDraft((d) => ({ ...d, status: opt.key }))}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all border"
                    style={{
                      background: isActive ? '#EEF2FF' : '#fff',
                      color: isActive ? '#6366F1' : '#6B7280',
                      borderColor: isActive ? '#6366F1' : '#E5E7EB',
                    }}
                  >
                    <i className={`${opt.icon} text-[11px]`} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
